import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import { z } from "zod";

export type ClientTeamJSON = z.infer<typeof ClientTeamSchema.ClientTeamJSONZ>;
export type ClientTeamParams = z.infer<typeof ClientTeamSchema.ClientTeamZ>;

export default class ClientTeamSchema {
  public static readonly ClientTeamJSONZ = z.object({
    leader: EntityMemberSchema.EntityMemberJSONZ.nullish().optional(),
    members: z.array(EntityMemberSchema.EntityMemberJSONZ).optional(),
  });

  public static readonly ClientTeamZ = z.object({
    leader: EntityMemberSchema.EntityMemberZ.optional(),
    members: z.array(EntityMemberSchema.EntityMemberZ),
  });

  public static validate(values: unknown): ClientTeamParams {
    return parseOrThrow(ClientTeamSchema.ClientTeamZ, values, {
      logError: true,
      schemaName: "ClientTeam",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): ClientTeamJSON {
    return parseOrThrow(ClientTeamSchema.ClientTeamJSONZ, json, {
      logError: true,
      schemaName: "ClientTeam",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is ClientTeamParams {
    try {
      ClientTeamSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is ClientTeamJSON {
    try {
      ClientTeamSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: ClientTeamJSON): ClientTeamParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(ClientTeamSchema.ClientTeamJSONZ, json, {
      logError: true,
      schemaName: "ClientTeam",
      operation: "deserialize",
      payloadSample: json,
    });
    return ClientTeamSchema.codec.decode(parsed);
  }

  public static serialize(params: ClientTeamParams): ClientTeamJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = ClientTeamSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return ClientTeamSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(ClientTeamSchema.ClientTeamJSONZ, ClientTeamSchema.ClientTeamZ, {
    decode: (data) => ({
      leader: data.leader ? EntityMemberSchema.deserialize(data.leader) : undefined,
      members: data.members?.map((member) => EntityMemberSchema.deserialize(member)) ?? [],
    }),
    encode: (entity) => ({
      leader: entity.leader ? EntityMemberSchema.serialize(entity.leader) : undefined,
      members: entity.members.map((member) => EntityMemberSchema.serialize(member)),
    }),
  });
}
